import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { clsx } from "clsx";
import Button from "./Button";

const navLinks = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Security", href: "#security" },
  { label: "Pricing", href: "#pricing" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header
      className={clsx(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-bg-primary/80 backdrop-blur-xl border-b border-surface-border/50"
          : "bg-transparent"
      )}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Left: Logo + Nav Links */}
          <div className="flex items-center gap-10">
            <a href="#" className="flex items-center gap-2 group">
              <img
                src="/logo.png"
                alt="HirenFire"
                className="h-10 w-auto drop-shadow-[0_0_8px_rgba(59,130,246,0.5)] group-hover:drop-shadow-[0_0_12px_rgba(124,58,237,0.6)] transition-all duration-300"
              />
              <span className="text-xl font-display font-bold tracking-tight">
                <span className="text-white">HIREN</span>
                <span className="text-gradient-bold">FIRE</span>
              </span>
            </a>

            {/* Desktop Nav Links — inline with logo */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          {/* Right: Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <Button variant="text" size="sm" href="#cta">
              Find Work →
            </Button>
            <Button variant="gradient" size="sm" href="#cta">
              Start Hiring
            </Button>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={clsx(
          "md:hidden fixed inset-0 top-16 z-40 transition-all duration-300",
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        )}
      >
        <div
          className="absolute inset-0 bg-black/60"
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={clsx(
            "relative mx-4 mt-2 rounded-2xl border border-surface-border/50 bg-bg-primary/95 backdrop-blur-xl p-6 transition-transform duration-300",
            mobileOpen ? "translate-y-0" : "-translate-y-4"
          )}
        >
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-lg text-gray-300 hover:text-white transition-colors py-2"
              >
                {link.label}
              </a>
            ))}
            <hr className="border-surface-border" />
            <div className="flex flex-col gap-3 pt-2">
              <Button
                variant="text"
                size="md"
                href="#cta"
                onClick={() => setMobileOpen(false)}
              >
                Find Work →
              </Button>
              <Button
                variant="gradient"
                size="md"
                href="#cta"
                onClick={() => setMobileOpen(false)}
              >
                Start Hiring
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
